import { inject, Injectable } from '@angular/core';
import { ApplicationHelperService } from 'app/modules/forms/application-helper.service';
import { ControlHelperService } from 'app/modules/forms/control-helper.service';
import Big from 'big.js';
import { CompareDefinition, ComparisonType, DataContainer, Operator, Qualifier, RuleType, TruthCondition, iApplyFormControl } from '../../model';
import { rule_IN } from './rule.in';
import { rule_NOTIN } from './rule.not-in';

@Injectable()
export class RuleEvaluatorService
{
    private applicationHelperService = inject(ApplicationHelperService);
    private controlHelperService = inject(ControlHelperService);

    evalOperations = {
        EQ: function (value1, value2) {
            const isEmptyValue = (val) => {
                if (val === undefined || val === null || val === '') {
                    return true;
                }
                if (Array.isArray(val)) {
                    return val.length === 0 || val.every(v => isEmptyValue(v));
                }
                return false;
            };

            if (isEmptyValue(value1) && isEmptyValue(value2)) {
                return true;
            }
            if (isEmptyValue(value1) || isEmptyValue(value2)) {
                return false;
            }

            const cleanArr1 = Array.isArray(value1) ? value1.filter(v => !isEmptyValue(v)) : [value1];
            const cleanArr2 = Array.isArray(value2) ? value2.filter(v => !isEmptyValue(v)) : [value2];

            if (cleanArr1.length === 1 && cleanArr2.length === 1) {
                return cleanArr1[0] === cleanArr2[0];
            }

            if (cleanArr1.length !== cleanArr2.length) {
                return false;
            }

            return cleanArr1.every(v => cleanArr2.includes(v));
        },

        NE: function (value1, value2) {
            return !this.EQ(value1, value2);
        },

        IN: rule_IN,

        NIN: rule_NOTIN,

        // TODO: GT(E) / LT(E): use "_comparable(...)" to determine the "numeric/size/legnth" of the value?
        // or, keep using the string comparison?
        GT: function (value1, value2) {
            return value1 > value2;
        },

        GTE: function (value1, value2) {
            return value1 >= value2;
        },

        LT: function (value1, value2) {
            return value1 < value2;
        },

        LTE: function (value1, value2) {
            return value1 <= value2;
        },

        CONTAINS: function (value1, value2) {
            if (!Array.isArray(value1)) {
                value1 = [value1];
            }

            if (!Array.isArray(value2)) {
                return (<Array<any>>value1).some(v => v.toString().toLowerCase() === value2.toString().toLowerCase());
            }

            return (<Array<any>>value1).some(v =>
                Array.isArray(v) && v.length && (<Array<any>>value2).every(item => v.indexOf(item) >= 0));
        },

        NOTCONTAINS: function (value1, value2) {
            return !this.CONTAINS(value1, value2);
        }
    };
    totalSearchPattern = /[.]Total$/;
    totalReplacePattern = /^([^.]*)[.]*([^.]*)[.]Total$/;
    rowCountSearchPattern = /[.]RowCount$/;
    rowCountReplacePattern = /^([^.]*)[.]*([^.]*)[.]RowCount$/;
    totalScoreSearchPattern = /^\$TotalScore$/;
    totalScoreAvailableSearchPattern = /^\$TotalScoreAvailable$/;
    totalScorePercentageSearchPattern = /^\$TotalScorePercentage$/;

    _comparable(value) {
        if (value === undefined || value === null) {
            return "";
        }

        if (typeof value === 'number') {
            return value.toString(); //.toString().length;
        }

        if (Array.isArray(value)) {
            return value.length;
        }

        if (typeof value === 'string') {
            // TODO: For strings, do we want to do a locale specific GT/LT compare or a string length compare?
            // e.g. "Apple" is LT "Orange", but "Apple" is GT "apple" because of upper case vs lower case.
            return value.length;
        }

        if (typeof value === 'object') {
            return Object.keys(value).length.toString(); // value.length;
            // let count = 0;
            // for (let i in value) {
            //     if (value.hasOwnProperty(i)) {
            //         count++;
            //     }
            // }
            // return count;
        }

        return value; // ('length' in value) ? value.length : 0;
    }

    _evaluateAnd(ruleList: TruthCondition[], container: DataContainer, qualifier: Qualifier, surveyResult, controlList: iApplyFormControl[] | iApplyFormControl[][]) {
        let ruleListlength = ruleList.length;
        for (let i = 0; i < ruleListlength; i++) {
            if (!this.evaluate(ruleList[i], container, qualifier, surveyResult, controlList)) {
                return false;
            }
        }
        return true;
    }

    _evaluateOr(ruleList: TruthCondition[], container: DataContainer, qualifier: Qualifier, surveyResult, controlList: iApplyFormControl[] | iApplyFormControl[][]) {
        let ruleListlength = ruleList.length;
        for (let i = 0; i < ruleListlength; i++) {
            if (this.evaluate(ruleList[i], container, qualifier, surveyResult, controlList)) {
                return true;
            }
        }
        return false;
    }

    _evaluateEval(rule: TruthCondition, container: DataContainer, qualifier: Qualifier, surveyResult, controlList: iApplyFormControl[]) {
        let ruleValue = <CompareDefinition>rule.value,
            isTotal = this.totalSearchPattern.test(ruleValue.field),
            isRowCount = this.rowCountSearchPattern.test(ruleValue.field),
            isSurveyTotalScore = this.totalScoreSearchPattern.test(ruleValue.field),
            isSurveyTotalScoreAvailable = this.totalScoreAvailableSearchPattern.test(ruleValue.field),
            isSurveyTotalScorePercentage = this.totalScorePercentageSearchPattern.test(ruleValue.field),
            resultObj,
            compareValue = (ruleValue.compareTo === ComparisonType.Value) ? ruleValue.value : this.controlHelperService.findByName(controlList, ruleValue.value);

        if (isTotal) {
            resultObj = this.getTotalValue(ruleValue, container, qualifier);
        }
        else if (isRowCount) {
            resultObj = this.getRowCountValue(ruleValue, container, qualifier);
        }
        else if (isSurveyTotalScore) {
            resultObj = {
                SubField: ruleValue.subField,
                ActualValue: surveyResult.TotalScore
            };
        }
        else if (isSurveyTotalScoreAvailable) {
            resultObj = {
                SubField: ruleValue.subField,
                ActualValue: surveyResult.TotalScoreAvailable
            };
        }
        else if (isSurveyTotalScorePercentage) {
            resultObj = {
                SubField: ruleValue.subField,
                ActualValue: surveyResult.TotalScorePercentage
            };
        }
        else {
            if (ruleValue.operator === Operator.CONTAINS || ruleValue.operator === Operator.NOTCONTAINS) {
                if (qualifier && qualifier.paths && qualifier.paths.length) {
                    qualifier.paths[qualifier.paths.length - 1].index = null;
                } else {
                    qualifier = new Qualifier();
                }
            }
            resultObj = this.getStandardValue(ruleValue, container, qualifier, controlList);
        }

        if (resultObj.SubField) {
            resultObj.ActualValue = this.getSubFieldValue(resultObj.SubField, resultObj.ActualValue);
        }
        else {
            resultObj.ActualValue = this._cleanValue(resultObj.ActualValue);
        }

        compareValue = this._cleanValue(compareValue);
        return this.evalOperations[ruleValue.operator](resultObj.ActualValue, compareValue);
    }

    getSubFieldValue(subFieldName, fieldContents) {
        switch (subFieldName) {
            case 'latitude':
                let coords = fieldContents.split(',');
                if (coords.length !== 2) { return fieldContents; }
                return coords[0];
            case 'longitude':
                coords = fieldContents.split(',');
                if (coords.length !== 2) { return fieldContents; }
                return coords[1];
            case 'default':
                return fieldContents;
        }

        return fieldContents;
    }

    getStandardValue(ruleValue: CompareDefinition, container: DataContainer, qualifier: Qualifier, controlList: iApplyFormControl[]) {
        return {
            SubField: ruleValue.subField,
            ActualValue: this.applicationHelperService.findFieldValue(qualifier, ruleValue.field, container, controlList)
        };
    }

    _cleanValue(value) {
        if (value === undefined || value === null) {
            return '';
        }

        if (Array.isArray(value)) {
            let valuelength = value.length,
                returnValue = new Array(valuelength);

            for (let i = 0; i < valuelength; i++) {
                returnValue[i] = this._cleanValue(value[i]);
            }

            return returnValue;
        }

        if (/^[0-9]{4}\/[0-9]{2}\/[0-9]{2}$/.test(value) ||
            /^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/.test(value)) {
            // Convert to proper date types, which can later be compared as numbers.
            let date = new Date().convertRelativeDateString(value);
            if (!!date) {
                return date.getTime();
            }
        }

        if (/^[+-]?\d*\.?\d+$/.test(value)) {
            let numericValue = parseFloat(value);
            return !isNaN(numericValue) ? numericValue : value;
        }

        return value;
    }

    getRowCountValue(ruleValue: CompareDefinition, container: DataContainer, qualifier: Qualifier) {
        let fieldName = ruleValue.field.replace(this.rowCountReplacePattern, '$1'),
            fieldValue = this.applicationHelperService.findFieldValue(qualifier, fieldName, container) || [];

        return {
            SubField: ruleValue.subField,
            ActualValue: fieldValue.length
        };
    }

    findRuleDependencies(rule: TruthCondition, dependenciesToConcreteField, dependenciesToFieldCollection) {
        let ruleValue = (<TruthCondition>rule).value;
        if ((<TruthCondition>rule).ruleType.toUpperCase() === RuleType.Eval) {
            let compareDef: CompareDefinition = <any>ruleValue;
            let arr = compareDef.operator === Operator.CONTAINS || compareDef.operator === Operator.NOTCONTAINS
                ? dependenciesToFieldCollection
                : dependenciesToConcreteField;
            if (!arr.includes(compareDef.field)) {
                arr.push(compareDef.field);
            }
            if (compareDef.compareTo === 'Field' && !arr.includes(compareDef.value)) {
                arr.push(compareDef.value);
            }
        } else {
            for (let i = 0; i < (<TruthCondition[]>ruleValue).length; i++) {
                this.findRuleDependencies(ruleValue[i], dependenciesToConcreteField, dependenciesToFieldCollection);
            }
        }
    };

    getTotalValue(ruleValue: CompareDefinition, container: DataContainer, qualifier: Qualifier) {
        let fieldName = ruleValue.field.replace(this.totalReplacePattern, '$2'),
            subFieldName = ruleValue.subField ? ruleValue.subField.replace(this.totalReplacePattern, '$2') : ruleValue.subField,
            parentName = ruleValue.field.replace(this.totalReplacePattern, '$1'),
            fieldValue = this.applicationHelperService.findFieldValue(qualifier, parentName, container);

        return {
            SubField: subFieldName,
            ActualValue: this.sumBigValues(fieldValue, fieldName)
        };
    }

    sumBigValues(input, params) {
        let totalSum = new Big(0);
        for (let i = 0; i < (input || []).length; i++) {
            let add = parseFloat(input[i][params]);
            add = isNaN(add) ? 0 : add;
            add = new Big(add);
            totalSum = totalSum.plus(add);
        }
        return totalSum.toString();
    }

    evaluate(rule: TruthCondition, application: DataContainer, qualifier?: Qualifier, surveyResult?, controlList?: iApplyFormControl[] | iApplyFormControl[][]) {
        application = application || {};
        surveyResult = surveyResult || {};

        switch (rule.ruleType.toUpperCase()) {
            case 'OR':
                return this._evaluateOr(<TruthCondition[]>rule.value, application, qualifier, surveyResult, controlList);
            case 'EVAL':
                return this._evaluateEval(rule, application, qualifier, surveyResult, <iApplyFormControl[]>controlList);
            default:
                return this._evaluateAnd(<TruthCondition[]>rule.value, application, qualifier, surveyResult, controlList);
        }
    };
}

